import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Prakash Electrodes Private Limited';
const DEFAULT_DESC =
  'Premium welding electrodes manufacturer in India since 1985. WELDARC, CUTWELD, HITKARI GOLD, SUNLIGHT-13, BPL brands. IS 814:2004 certified. Based in Kota, Rajasthan.';
const SITE_ORIGIN = process.env.REACT_APP_SITE_URL;
const DEFAULT_IMG =
  'https://images.unsplash.com/photo-1698664683348-f9f35b809821?w=1200&auto=format&fit=crop&q=80';

export default function SEO({
  title,
  description = DEFAULT_DESC,
  image = DEFAULT_IMG,
  path,
  type = 'website',
  jsonLd,
  keywords,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Welding Electrodes Since 1985`;
  const url = path !== undefined ? `${SITE_ORIGIN}${path}` : null;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(', ')} />
      )}
      {url && <link rel="canonical" href={url} />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      {url && <meta property="og:url" content={url} />}
      <meta property="og:type" content={type} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLd &&
        (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map((block, i) => (
          <script key={i} type="application/ld+json">
            {JSON.stringify(block)}
          </script>
        ))}
    </Helmet>
  );
}

export const orgJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Prakash Electrodes Private Limited',
  legalName: 'Prakash Electrodes Private Limited',
  url: SITE_ORIGIN,
  foundingDate: '1985',
  founders: [{ '@type': 'Person', name: 'Prakash Family' }],
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'F-147 IPIA, Road no.5',
    addressLocality: 'Kota',
    addressRegion: 'Rajasthan',
    addressCountry: 'IN',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+91-77371-00051',
    contactType: 'sales',
    email: 'prakashelectrodes@gmail.com',
  },
  sameAs: [],
});

export const articleJsonLd = (post) => ({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: post.title,
  description: post.excerpt,
  image: post.image,
  datePublished: post.created_at || post.date,
  author: { '@type': 'Person', name: post.author },
  publisher: {
    '@type': 'Organization',
    name: SITE_NAME,
  },
  keywords: (post.keywords || []).join(', '),
});

// ---- Data-driven builders (generated from mock.js content) ----

const _origin = () => SITE_ORIGIN;

// Helps Google understand the site entity (and enables a sitelinks searchbox
// later, once an on-site search endpoint exists).
export const websiteJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: _origin(),
});

// items: [{ name, path }] in order, e.g. [{name:'Home',path:'/'}, ...]
export const breadcrumbJsonLd = (items = []) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: `${_origin()}${it.path}`,
  })),
});

// faqs: [{ q, a }] — eligible for FAQ rich results in Google.
export const faqJsonLd = (faqs = []) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});

// products: mock.js products array. No price/rating is emitted on purpose —
// fabricating offers or reviews risks Google structured-data penalties.
export const productListJsonLd = (products = []) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  itemListElement: products.map((p, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    item: {
      '@type': 'Product',
      name: p.name,
      description: p.description,
      image: p.image,
      category: 'Welding Electrodes',
      brand: { '@type': 'Brand', name: p.name },
      manufacturer: { '@type': 'Organization', name: SITE_NAME },
      additionalProperty: (p.features || []).map((value) => ({
        '@type': 'PropertyValue',
        name: 'Feature',
        value,
      })),
    },
  })),
});

// Local relevance for a city page. NOTE: the business is physically in Kota, so
// we emit an Organization located in Kota with `areaServed` set to the city —
// NOT a fake LocalBusiness "located" in each city (which would be misleading and
// risky). This is the honest, correct way to signal local service coverage.
export const localBusinessJsonLd = ({ city, state } = {}) => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: _origin(),
  foundingDate: '1985',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'F-147 IPIA, Road no.5',
    addressLocality: 'Kota',
    addressRegion: 'Rajasthan',
    postalCode: '324005',
    addressCountry: 'IN',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+91-77371-00051',
    contactType: 'sales',
    email: 'prakashelectrodes@gmail.com',
    areaServed: 'IN',
  },
  areaServed: city
    ? { '@type': 'City', name: state ? `${city}, ${state}` : city }
    : 'IN',
});

// places: [{ city, state, path }] — an ItemList of the location directory.
export const placesListJsonLd = (places = []) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  itemListElement: places.map((p, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: `Welding Electrodes in ${p.city}`,
    url: `${_origin()}${p.path}`,
  })),
});
